import { FunctionComponent, h } from 'preact';
import { get as _get, isNil as _isNil } from 'lodash';
import { observer } from 'mobx-react';
import { useCallback, useState, useMemo } from 'preact/hooks';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import bringgDefaultLogo from '../../../assets/bringg-logo-app.svg';
import MerchantLogo from '../../../components/merchant-logo/merchant-logo';
import PoweredByLogo from '../../../components/svg-components/powered-by-svg';
import { ButtonsConfig } from '../../../components/side-nav/side-nav';
import SharedLocationSideNav from '../sidenav/shared-location-side-nav';
import TrackingStore from '../../../stores/tracking/tracking-store';
import { useTranslation } from '../../../hooks/use-translation';
import { getIconVisibility } from '../../../helpers/aria-helper';
import { useStore } from '../../../helpers/use-store';
import { isScheduleShareType } from '../../../helpers/sharing-type-helper';

interface Props {
	merchantLogo?: string;
}

// TODO: remove when doing new API refactor
export const OLD_BRINGG_LOGO = '/images/logo.png';
const isOldBringgLogo = (merchantLogo: string) => merchantLogo.includes(OLD_BRINGG_LOGO);

const SharedLocationHeader: FunctionComponent<Props> = ({ merchantLogo }) => {
	const { t } = useTranslation();
	const { ariaStore, sharedLocationStore, customerExperienceStore } = useStore();
	const shouldUseBringgLogo = _isNil(merchantLogo) || isOldBringgLogo(merchantLogo);
	const merchantLogoUrl = shouldUseBringgLogo ? bringgDefaultLogo : merchantLogo;

	const hidePoweredByLogo = _get(
		customerExperienceStore.customerExperience,
		['ui_data', 'hide_powered_by_logo'],
		false
	);

	const [isSideNavOpen, setIsSideNavOpen] = useState<boolean>(false);

	const openSideNav = useCallback(() => {
		setIsSideNavOpen(true);
		ariaStore.setIsModalOpen(true);
	}, [ariaStore]);

	const closeSideNav = useCallback(() => {
		setIsSideNavOpen(false);
		ariaStore.setIsModalOpen(false);
	}, [ariaStore]);

	const sideNavButtonsConfig = useMemo(() => {
		let buttonsConfig: ButtonsConfig = null;

		const sharedType = _get(sharedLocationStore.sharedLocationConfig, 'shared_type');
		const displayType = _get(sharedLocationStore.sharedLocationConfig, 'display_type');
		const shareLocationEnabled = _get(sharedLocationStore.sharedLocationConfig, 'support_find_me', false);
		const forwardEnabled = _get(sharedLocationStore.sharedLocationConfig, 'task_forwarding_enabled', false);

		if (forwardEnabled) {
			buttonsConfig = {
				...buttonsConfig,
				forward: {
					text: t('FORWARD.THIS_ORDER'),
					icon: BringgFontIcons.Forward,
					onClick: sharedLocationStore.forwardMode
				}
			};
		}

		if (shareLocationEnabled && !isScheduleShareType({ sharedType, displayType })) {
			const handleShareLocation = useCallback(async () => {
				const trackingStore: TrackingStore = sharedLocationStore.getActiveTrackingStore();
				await trackingStore.shareUserLocation();
				closeSideNav();
			}, [sharedLocationStore]);

			buttonsConfig = {
				...buttonsConfig,
				share: {
					text: t('SHARING_MY_LOCATION'),
					icon: BringgFontIcons.MyLocation,
					onClick: handleShareLocation,
					loadingConfig: {
						icon: BringgFontIcons.Loader,
						text: t('SHARING_LOADING')
					}
				}
			};
		}
		return buttonsConfig;
	}, [sharedLocationStore, sharedLocationStore.sharedLocationConfig]);

	return (
		<div className="shared-location-header" data-testid="shared-location-header">
			<button
				aria-label={t('OPEN_MENU')}
				onClick={openSideNav}
				style={getIconVisibility(Boolean(sideNavButtonsConfig) && !isSideNavOpen && !ariaStore.isModalOpen)}
				className={'menu'}
				tabIndex={isSideNavOpen || ariaStore.isModalOpen ? -1 : 0}
			>
				<BringgIcon iconName={BringgFontIcons.Hamburger} />
			</button>
			<MerchantLogo className="merchant-logo-header" merchantLogoUrl={merchantLogoUrl} />
			{hidePoweredByLogo ? null : <PoweredByLogo className="powered-by-logo" />}
			<SharedLocationSideNav
				aria-hidden={ariaStore.isModalOpen}
				hidePoweredByLogo={hidePoweredByLogo}
				isOpen={isSideNavOpen}
				onClose={closeSideNav}
				merchantLogoUrl={merchantLogoUrl}
				buttonsConfig={sideNavButtonsConfig}
			/>
		</div>
	);
};

export default observer(SharedLocationHeader);
